import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { contentAPI } from '../services/api';
import ReactMarkdown from 'react-markdown';
import useNavigationWarning from '../hooks/useNavigationWarning';
import { checkAuthBeforeAction, isAuthenticated } from '../utils/authHelper';
import NavigationWarningModal from '../components/NavigationWarningModal';
import usePermission from '../hooks/usePermission';
import UpgradePrompt from '../components/UpgradePrompt';

const AIAvatar = () => {
  const navigate = useNavigate();

  // Permission check
  const { hasModelAccess, getPlanInfo, getRequiredPlanForModel } = usePermission();

  // Upgrade prompt state
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradePromptData, setUpgradePromptData] = useState({});

  // Conversation management
  const [conversations, setConversations] = useState([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [inputMessage, setInputMessage] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Get active conversation
  const activeConversation = conversations.find(c => c.id === activeConversationId) || conversations[0];
  const messages = activeConversation?.messages || [];

  // Model display mapping
  const getModelDisplayInfo = (modelName) => {
    const modelMap = {
      'deepseek-chat': {
        displayName: 'Basic - Fast Response',
        tier: 'Basic',
        description: 'Quick and efficient for everyday tasks',
        icon: '⚡',
        badge: 'Basic'
      },
      'deepseek-reasoner': {
        displayName: 'Basic - Deep Thinking',
        tier: 'Basic',
        description: 'Advanced reasoning for complex problems',
        icon: '🧠',
        badge: 'Basic'
      },
      'doubao-seed-1-6': {
        displayName: 'Professional - Vision Pro',
        tier: 'Professional',
        description: 'Image understanding + text generation',
        icon: '👁️',
        badge: 'Pro'
      },
      'qwen3-vl-plus': {
        displayName: 'Professional - Vision Plus',
        tier: 'Professional',
        description: 'Advanced multimodal AI with vision',
        icon: '🎯',
        badge: 'Pro'
      }
    };

    return modelMap[modelName] || {
      displayName: modelName,
      tier: 'Standard',
      description: 'AI Model',
      icon: '🤖',
      badge: null
    };
  };

  // Load conversations from backend on mount
  useEffect(() => {
    const loadConversations = async () => {
      try {
        setIsLoadingConversations(true);
        const response = await contentAPI.getConversations();

        if (response.data.success && response.data.data.length > 0) {
          // Parse messages from JSONB
          const loadedConversations = response.data.data.map(conv => ({
            ...conv,
            messages: typeof conv.messages === 'string' ? JSON.parse(conv.messages) : conv.messages
          }));
          setConversations(loadedConversations);
          setActiveConversationId(loadedConversations[0].id);
        } else {
          // No conversations exist, create default one
          const defaultConversation = {
            id: `conv-${Date.now()}`,
            title: 'New Conversation',
            messages: [{
              id: 1,
              type: 'ai',
              content: 'Hello! How can I help you today?',
              timestamp: new Date().toISOString()
            }]
          };

          // Save to backend
          await contentAPI.createConversation(defaultConversation);
          setConversations([defaultConversation]);
          setActiveConversationId(defaultConversation.id);
        }
      } catch (error) {
        console.error('Failed to load conversations:', error);
        // Create default conversation locally on error
        const defaultConversation = {
          id: `conv-${Date.now()}`,
          title: 'New Conversation',
          messages: [{
            id: 1,
            type: 'ai',
            content: 'Hello! How can I help you today?',
            timestamp: new Date().toISOString()
          }]
        };
        setConversations([defaultConversation]);
        setActiveConversationId(defaultConversation.id);
      } finally {
        setIsLoadingConversations(false);
      }
    };

    loadConversations();
  }, []);

  // Load available AI models on mount
  useEffect(() => {
    const loadModels = async () => {
      try {
        const response = await contentAPI.getAvailableModels();
        const models = response.data.data;
        setAvailableModels(models);
        // Set deepseek-chat as default, fallback to first model
        if (models.length > 0 && !selectedModel) {
          const defaultModel = models.find(m => m.name === 'deepseek-chat') || models[0];
          setSelectedModel(defaultModel);
        }
      } catch (error) {
        console.error('Failed to load models:', error);
        // Provide default models on error (for unauthenticated users)
        const defaultModels = [
          { name: 'deepseek-chat', displayName: 'Basic - Fast Response' },
          { name: 'deepseek-reasoner', displayName: 'Basic - Deep Thinking' }
        ];
        setAvailableModels(defaultModels);
        if (!selectedModel) {
          setSelectedModel(defaultModels[0]);
        }
      }
    };
    loadModels();
  }, [selectedModel]);

  // Auto scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Show warning when user tries to leave during AI generation
  const navigationWarning = useNavigationWarning(
    isLoading,
    'AI is generating a response. If you leave now, the response will continue in the background.',
    'ai-chat'
  );

  // Create new conversation
  const createNewConversation = async () => {
    const newConversation = {
      id: `conv-${Date.now()}`,
      title: 'New Conversation',
      messages: [{
        id: Date.now(),
        type: 'ai',
        content: 'Hello! How can I help you today?',
        timestamp: new Date().toISOString()
      }]
    };

    try {
      await contentAPI.createConversation(newConversation);
      setConversations(prev => [newConversation, ...prev]);
      setActiveConversationId(newConversation.id);
    } catch (error) {
      console.error('Failed to create conversation:', error);
      // Still create locally for better UX
      setConversations(prev => [newConversation, ...prev]);
      setActiveConversationId(newConversation.id);
    }
  };

  // Delete conversation
  const deleteConversation = async (id) => {
    if (conversations.length === 1) {
      // Don't delete the last conversation, just create a new one
      await createNewConversation();
      try {
        await contentAPI.deleteConversation(id);
      } catch (error) {
        console.error('Failed to delete conversation:', error);
      }
      return;
    }

    try {
      await contentAPI.deleteConversation(id);
      const newConversations = conversations.filter(c => c.id !== id);
      setConversations(newConversations);

      if (activeConversationId === id) {
        setActiveConversationId(newConversations[0].id);
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      // Still delete locally for better UX
      const newConversations = conversations.filter(c => c.id !== id);
      setConversations(newConversations);

      if (activeConversationId === id) {
        setActiveConversationId(newConversations[0].id);
      }
    }
  };

  // Helper function to save conversation to backend
  const saveConversationToBackend = async (conversationId, updates) => {
    try {
      await contentAPI.updateConversation(conversationId, updates);
    } catch (error) {
      console.error('Failed to save conversation to backend:', error);
      // Continue with local state - don't interrupt user experience
    }
  };

  // Update conversation title based on first message
  const updateConversationTitle = (conversationId, firstUserMessage) => {
    const newTitle = firstUserMessage.substring(0, 30) + (firstUserMessage.length > 30 ? '...' : '');

    setConversations(prev => prev.map(conv => {
      if (conv.id === conversationId && conv.title === 'New Conversation') {
        // Save to backend
        saveConversationToBackend(conversationId, { title: newTitle });

        return {
          ...conv,
          title: newTitle
        };
      }
      return conv;
    }));
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);

    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setSelectedImages(prev => [...prev, {
            id: Date.now() + Math.random(),
            file: file,
            preview: event.target.result,
            name: file.name
          }]);
        };
        reader.readAsDataURL(file);
      }
    });

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove selected image
  const removeImage = (imageId) => {
    setSelectedImages(prev => prev.filter(img => img.id !== imageId));
  };

  // Handle paste event for images
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // Check if item is an image
      if (item.type.startsWith('image/')) {
        e.preventDefault(); // Prevent default paste behavior for images

        const file = item.getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            setSelectedImages(prev => [...prev, {
              id: Date.now() + Math.random(),
              file: file,
              preview: event.target.result,
              name: `pasted-image-${Date.now()}.png`
            }]);
          };
          reader.readAsDataURL(file);
        }
      }
    }
  };

  // Handle drag and drop for images
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files);
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setSelectedImages(prev => [...prev, {
            id: Date.now() + Math.random(),
            file: file,
            preview: event.target.result,
            name: file.name
          }]);
        };
        reader.readAsDataURL(file);
      }
    });
  };

  // Auto resize textarea
  const handleInputChange = (e) => {
    setInputMessage(e.target.value);

    // Auto resize
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  };

  // Copy message to clipboard
  const handleCopyMessage = async (content) => {
    try {
      await navigator.clipboard.writeText(content);
      // You can add a toast notification here if needed
      console.log('Message copied to clipboard');
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  };

  // Regenerate AI response
  const handleRegenerateResponse = async (messageIndex) => {
    if (isLoading) return;

    const currentMessages = messages;

    // Find the user message before this AI message
    let userMessageContent = null;
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (currentMessages[i].type === 'user') {
        userMessageContent = currentMessages[i].content;
        break;
      }
    }

    if (!userMessageContent) {
      console.error('No user message found to regenerate response');
      return;
    }

    // Remove the current AI message
    setConversations(prev => prev.map(conv => {
      if (conv.id === activeConversationId) {
        const updatedMessages = [...conv.messages];
        updatedMessages.splice(messageIndex, 1);
        return {
          ...conv,
          messages: updatedMessages,
          updatedAt: new Date().toISOString()
        };
      }
      return conv;
    }));

    setIsLoading(true);

    // Regenerate AI response
    try {
      const activeConv = conversations.find(c => c.id === activeConversationId);
      const validHistory = (activeConv?.messages.slice(0, messageIndex - 1) || [])
        .filter(msg => msg.content && msg.content.trim())
        .map(msg => ({
          type: msg.type,
          content: msg.content.trim()
        }));

      const response = await contentAPI.chatWithAIAvatar({
        messages: [{ type: 'user', content: userMessageContent }],
        conversation_history: validHistory
      });

      const aiMessage = {
        id: Date.now(),
        type: 'ai',
        content: response.data.data.response,
        timestamp: new Date().toISOString()
      };

      setConversations(prev => prev.map(conv => {
        if (conv.id === activeConversationId) {
          return {
            ...conv,
            messages: [...conv.messages, aiMessage],
            updatedAt: new Date().toISOString()
          };
        }
        return conv;
      }));
    } catch (error) {
      console.error('Regenerate error:', error);

      let errorContent = 'Sorry, I encountered an error. Please try again later.';
      if (error.response?.data?.error?.message) {
        errorContent = error.response.data.error.message;
      }

      const errorMessage = {
        id: Date.now(),
        type: 'ai',
        content: errorContent,
        timestamp: new Date().toISOString()
      };

      setConversations(prev => prev.map(conv => {
        if (conv.id === activeConversationId) {
          return {
            ...conv,
            messages: [...conv.messages, errorMessage],
            updatedAt: new Date().toISOString()
          };
        }
        return conv;
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if ((!inputMessage.trim() && selectedImages.length === 0) || isLoading) return;

    // Check authentication before making API call
    if (!checkAuthBeforeAction(navigate)) {
      return;
    }

    // Check model permission before sending
    if (selectedModel && !hasModelAccess(selectedModel.name)) {
      const modelInfo = getModelDisplayInfo(selectedModel.name);
      const planInfo = getPlanInfo();
      setUpgradePromptData({
        type: 'model',
        featureName: modelInfo.displayName,
        currentPlan: planInfo.name,
        requiredPlan: getRequiredPlanForModel(selectedModel.name)
      });
      setShowUpgradePrompt(true);
      return;
    }

    // Prepare content - if only images, add a placeholder text
    const messageContent = inputMessage.trim() || (selectedImages.length > 0 ? 'What do you see in this image? Please describe it in detail.' : '');

    if (!messageContent && selectedImages.length === 0) return; // Safety check

    // Convert images to base64
    const imageBase64List = [];
    for (const img of selectedImages) {
      try {
        const base64 = await fetch(img.preview)
          .then(res => res.blob())
          .then(blob => {
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          });
        imageBase64List.push(base64);
      } catch (error) {
        console.error('Failed to convert image to base64:', error);
      }
    }

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: messageContent,
      images: selectedImages.map(img => ({
        preview: img.preview,
        name: img.name
      })),
      timestamp: new Date().toISOString()
    };

    // Update conversation with new message
    setConversations(prev => prev.map(conv => {
      if (conv.id === activeConversationId) {
        const updatedMessages = [...conv.messages, userMessage];

        // Save to backend
        saveConversationToBackend(activeConversationId, { messages: updatedMessages });

        return {
          ...conv,
          messages: updatedMessages
        };
      }
      return conv;
    }));

    // Update title if this is the first user message
    const activeConv = conversations.find(c => c.id === activeConversationId);
    if (activeConv && activeConv.title === 'New Conversation') {
      const titleText = inputMessage.trim() || 'Image message';
      updateConversationTitle(activeConversationId, titleText);
    }

    setInputMessage('');
    setSelectedImages([]);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    setIsLoading(true);

    // Call new async task API
    try {
      // Filter conversation history to only include messages with valid content
      const validHistory = (activeConv?.messages.slice(0, -1) || [])
        .filter(msg => msg.content && msg.content.trim())
        .map(msg => ({
          type: msg.type,
          content: msg.content.trim()
        }));

      // Prepare API request with images
      const apiRequest = {
        model_id: selectedModel?.id,
        messages: [{
          type: 'user',
          content: userMessage.content,
          images: imageBase64List.length > 0 ? imageBase64List : undefined
        }],
        conversation_id: activeConversationId,
        conversation_history: validHistory
      };

      // Create async task
      const taskResponse = await contentAPI.createChatTask(apiRequest);
      const taskId = taskResponse.data.data.task_id;

      console.log(`Created chat task ${taskId}, polling for result...`);

      // Poll for result (checks every 2 seconds, can survive page refresh)
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await contentAPI.getChatTask(taskId);
          const task = statusResponse.data.data;

          if (task.status === 'completed') {
            clearInterval(pollInterval);
            setIsLoading(false);

            const aiMessage = {
              id: Date.now() + 1,
              type: 'ai',
              content: task.response,
              timestamp: new Date().toISOString()
            };

            setConversations(prev => prev.map(conv => {
              if (conv.id === activeConversationId) {
                const updatedMessages = [...conv.messages, aiMessage];

                // Save to backend
                saveConversationToBackend(activeConversationId, { messages: updatedMessages });

                return {
                  ...conv,
                  messages: updatedMessages
                };
              }
              return conv;
            }));

            console.log(`Task ${taskId} completed successfully`);

          } else if (task.status === 'failed') {
            clearInterval(pollInterval);
            setIsLoading(false);

            const errorMessage = {
              id: Date.now() + 1,
              type: 'ai',
              content: task.error || 'Sorry, I encountered an error. Please try again later.',
              timestamp: new Date().toISOString()
            };

            setConversations(prev => prev.map(conv => {
              if (conv.id === activeConversationId) {
                const updatedMessages = [...conv.messages, errorMessage];

                // Save to backend
                saveConversationToBackend(activeConversationId, { messages: updatedMessages });

                return {
                  ...conv,
                  messages: updatedMessages
                };
              }
              return conv;
            }));

            console.error(`Task ${taskId} failed:`, task.error);
          }
          // Otherwise, task is still 'pending' or 'processing', keep polling
        } catch (pollError) {
          console.error('Error polling task:', pollError);
          // Don't stop polling on network errors, task may still complete
        }
      }, 2000); // Poll every 2 seconds

      // Stop polling after 3 minutes (safety timeout)
      setTimeout(() => {
        clearInterval(pollInterval);
        if (isLoading) {
          setIsLoading(false);
          const timeoutMessage = {
            id: Date.now() + 1,
            type: 'ai',
            content: 'The AI is taking longer than expected. The response may still be generated in the background.',
            timestamp: new Date().toISOString()
          };

          setConversations(prev => prev.map(conv => {
            if (conv.id === activeConversationId) {
              return {
                ...conv,
                messages: [...conv.messages, timeoutMessage],
                updatedAt: new Date().toISOString()
              };
            }
            return conv;
          }));
        }
      }, 180000); // 3 minutes
    } catch (error) {
      console.error('AI Avatar chat error:', error);
      setIsLoading(false); // Stop loading on error

      let errorContent = 'Sorry, I encountered an error. Please try again later.';

      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorContent = 'Request timed out. The AI is taking longer than expected to respond. Please try again.';
      } else if (error.response?.data?.error?.message) {
        errorContent = error.response.data.error.message;
      } else if (error.message) {
        errorContent = `Error: ${error.message}`;
      }

      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: errorContent,
        timestamp: new Date().toISOString()
      };

      setConversations(prev => prev.map(conv => {
        if (conv.id === activeConversationId) {
          return {
            ...conv,
            messages: [...conv.messages, errorMessage],
            updatedAt: new Date().toISOString()
          };
        }
        return conv;
      }));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      {/* Navigation Warning Modal */}
      <NavigationWarningModal
        isOpen={navigationWarning.showModal}
        onCancel={navigationWarning.handleCancelLeave}
        onConfirm={navigationWarning.handleConfirmLeave}
        message={navigationWarning.warningMessage}
        taskType={navigationWarning.taskType}
      />

      <div className="flex h-screen bg-gray-50">
      {/* Main Sidebar */}
      <Sidebar activeCategory="ai-avatar" />

      {/* Conversation History Sidebar */}
      <div className={`${showHistory ? 'w-64' : 'w-0'} bg-white border-r border-gray-200 flex flex-col transition-all duration-300 overflow-hidden`}>
        {/* History Header */}
        <div className="p-4 border-b border-gray-200">
          <button
            onClick={createNewConversation}
            className="w-full px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition flex items-center justify-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>New Chat</span>
          </button>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto p-2">
          {conversations.map((conversation) => (
            <div
              key={conversation.id}
              className={`group relative mb-1 rounded-lg transition cursor-pointer ${
                activeConversationId === conversation.id
                  ? 'bg-primary-50 border border-primary-200'
                  : 'hover:bg-gray-50 border border-transparent'
              }`}
            >
              <div
                onClick={() => setActiveConversationId(conversation.id)}
                className="px-3 py-3"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {conversation.title}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDate(conversation.updatedAt)}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteConversation(conversation.id);
                    }}
                    className="ml-2 opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition"
                    title="Delete conversation"
                  >
                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <Header />

        {/* Chat Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Chat Header */}
          <div className="border-b border-gray-200 px-6 py-2.5 bg-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition"
                  title={showHistory ? 'Hide history' : 'Show history'}
                >
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <h1 className="text-lg font-semibold text-gray-900">{activeConversation?.title || 'AI Avatar'}</h1>
              </div>

              {/* Model Selector */}
              <div className="flex items-center space-x-3">
                <span className="text-xs font-medium text-gray-500">AI Model:</span>
                <select
                  value={selectedModel?.id || ''}
                  onChange={(e) => {
                    const model = availableModels.find(m => m.id === e.target.value);
                    setSelectedModel(model);
                  }}
                  className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-medium bg-white hover:bg-gray-50 transition cursor-pointer appearance-none bg-no-repeat bg-right"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundSize: '1.25rem',
                    backgroundPosition: 'right 0.5rem center'
                  }}
                >
                  {availableModels.length === 0 && (
                    <option value="">Loading models...</option>
                  )}
                  {availableModels.map(model => {
                    const modelInfo = getModelDisplayInfo(model.name);
                    const hasAccess = hasModelAccess(model.name);
                    return (
                      <option key={model.id} value={model.id} disabled={!hasAccess}>
                        {modelInfo.icon} {modelInfo.displayName} {!hasAccess ? '🔒' : ''}
                      </option>
                    );
                  })}
                </select>

                {/* Model Badge - Show capability */}
                {selectedModel && (
                  <div className="flex items-center">
                    {(() => {
                      const modelInfo = getModelDisplayInfo(selectedModel.name);
                      const badgeColors = {
                        'Basic': 'bg-gray-100 text-gray-700 border-gray-300',
                        'Pro': 'bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 border-purple-300'
                      };
                      const badgeColor = badgeColors[modelInfo.badge] || 'bg-gray-100 text-gray-700';

                      return (
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${badgeColor}`}>
                          {modelInfo.badge}
                        </span>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto px-6 py-6 bg-gray-50">
            <div className="max-w-4xl mx-auto space-y-4">
              {messages.map((message, index) => (
                <div key={message.id}>
                  <div
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {/* Message Bubble */}
                    <div
                      className={`max-w-[65%] rounded-2xl px-4 py-3 ${
                        message.type === 'user'
                          ? 'bg-gray-100 text-gray-900 mr-4'
                          : 'bg-white text-gray-900 ml-4'
                      }`}
                    >
                      {/* Display images if any */}
                      {message.images && message.images.length > 0 && (
                        <div className="mb-3 grid grid-cols-2 gap-2">
                          {message.images.map((image, idx) => (
                            <div key={idx} className="rounded-lg overflow-hidden">
                              <img
                                src={typeof image === 'string' ? image : image.preview}
                                alt={typeof image === 'string' ? `AI generated image ${idx + 1}` : image.name}
                                className="w-full h-auto object-cover max-h-48"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  console.error('Failed to load image:', typeof image === 'string' ? image : image.preview);
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Display text content */}
                      {message.content && (
                        <div className="text-[15px] leading-relaxed prose prose-sm max-w-none">
                          <ReactMarkdown
                            components={{
                              // Custom styling for markdown elements
                              p: ({node, ...props}) => <p className="mb-2" {...props} />,
                              ul: ({node, ...props}) => <ul className="list-disc ml-4 mb-2" {...props} />,
                              ol: ({node, ...props}) => <ol className="list-decimal ml-4 mb-2" {...props} />,
                              li: ({node, ...props}) => <li className="mb-1" {...props} />,
                              strong: ({node, ...props}) => <strong className="font-semibold" {...props} />,
                              code: ({node, inline, ...props}) =>
                                inline ?
                                  <code className="bg-gray-100 px-1 py-0.5 rounded text-sm" {...props} /> :
                                  <code className="block bg-gray-100 p-2 rounded text-sm overflow-x-auto" {...props} />,
                              h1: ({node, ...props}) => <h1 className="text-xl font-bold mb-2" {...props} />,
                              h2: ({node, ...props}) => <h2 className="text-lg font-bold mb-2" {...props} />,
                              h3: ({node, ...props}) => <h3 className="text-base font-bold mb-2" {...props} />,
                            }}
                          >
                            {message.content}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons - Only for AI messages */}
                  {message.type === 'ai' && (
                    <div className="flex justify-start ml-4 mt-2">
                      <div className="flex items-center space-x-2">
                        {/* Copy Button */}
                        <button
                          onClick={() => handleCopyMessage(message.content)}
                          className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition"
                          title="复制"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>

                        {/* Regenerate Button */}
                        <button
                          onClick={() => handleRegenerateResponse(index)}
                          disabled={isLoading}
                          className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition disabled:opacity-30 disabled:cursor-not-allowed"
                          title="重新生成"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="max-w-[65%] rounded-2xl px-4 py-3 bg-white ml-4">
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area */}
          <div
            className="border-t border-gray-200 bg-white px-6 py-4"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div className="max-w-4xl mx-auto">
              {/* Image Preview Area */}
              {selectedImages.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {selectedImages.map((image) => (
                    <div key={image.id} className="relative group">
                      <img
                        src={image.preview}
                        alt={image.name}
                        className="h-16 w-16 object-cover rounded-lg border border-gray-200 shadow-sm"
                      />
                      <button
                        onClick={() => removeImage(image.id)}
                        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition shadow-sm"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Input Row */}
              <div className="flex items-end space-x-2">
                {/* Image Upload Button */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  accept="image/*"
                  multiple
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition flex-shrink-0 self-end mb-0.5"
                  title="Upload image"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>

                {/* Text Input */}
                <div className="flex-1 relative">
                  <textarea
                    ref={textareaRef}
                    value={inputMessage}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    onPaste={handlePaste}
                    placeholder="Type a message or paste an image..."
                    rows="1"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-[15px] leading-relaxed overflow-hidden"
                    style={{ minHeight: '44px', maxHeight: '150px' }}
                  />
                </div>

                {/* Send Button */}
                <button
                  onClick={handleSendMessage}
                  disabled={(!inputMessage.trim() && selectedImages.length === 0) || isLoading}
                  className="px-5 py-3 bg-blue-500 text-white font-medium rounded-xl hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center space-x-2 flex-shrink-0 self-end mb-0.5"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                    />
                  </svg>
                  <span>Send</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Prompt Modal */}
      <UpgradePrompt
        show={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        type={upgradePromptData.type}
        featureName={upgradePromptData.featureName}
        currentPlan={upgradePromptData.currentPlan}
        requiredPlan={upgradePromptData.requiredPlan}
      />
    </div>
    </>
  );
};

export default AIAvatar;
